import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface NeobrutalistInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const NeobrutalistInsights: React.FC<NeobrutalistInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify active bullet
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Quick scale/translate spring animation for text changes
  const textOpacity = interpolate(elapsedFrames, [0, 6], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.95,
    to: 1,
    config: { damping: 12, stiffness: 120, mass: 0.5 },
  });

  // Card entrance transition (first bullet start)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 10, stiffness: 90, mass: 0.8 },
  });

  // Bullet duration progress indicator
  const bulletDurationMs = activeBullet.endMs - activeBullet.startMs;
  const elapsedMs = Math.max(0, currentMs - activeBullet.startMs);
  const progressPct = Math.min(100, (elapsedMs / bulletDurationMs) * 100);

  // Background color alternates per bullet point to keep it dynamic and fresh!
  const backgroundColors = ["#FFFFFF", "#EC4899", "#A0E7FF"];
  const textColors = ["#000000", "#FFFFFF", "#000000"];
  const currentBg = backgroundColors[activeIndex % backgroundColors.length];
  const currentTextColor = textColors[activeIndex % textColors.length];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: currentBg,
          border: "5px solid #000000",
          boxShadow: "10px 10px 0px #000000",
          padding: "44px 36px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          position: "relative",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
          boxSizing: "border-box",
        }}
      >
        {/* Giant floating badge for the bullet index */}
        <div
          style={{
            position: "absolute",
            top: "-25px",
            right: "25px",
            width: "55px",
            height: "55px",
            background: "#FFE600",
            color: "#000000",
            border: "4px solid #000000",
            boxShadow: "4px 4px 0px #000000",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 900,
            fontSize: "24px",
            transform: "rotate(6deg)",
          }}
        >
          {activeIndex + 1}
        </div>

        {/* Brand badge row — neobrutalist style */}
        {(() => {
          const brand = detectBrandInText(activeBullet.text);
          return brand ? (
            <div style={{ alignSelf: "flex-start" }}>
              <CompanyBadge
                name={brand}
                color={currentTextColor}
                accentColor="#000000"
                size={72}
                style="badge"
                opacity={textOpacity}
                containerStyle={{
                  border: "3px solid #000000",
                  boxShadow: "4px 4px 0px #000000",
                  background: "#FFE600",
                }}
              />
            </div>
          ) : null;
        })()}

        {/* Bullet text with high-contrast highlighting */}
        <div
          style={{
            opacity: textOpacity,
            transform: `scale(${textScale})`,
            minHeight: "260px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "44px",
              fontWeight: 800,
              lineHeight: 1.4,
              color: currentTextColor,
              textAlign: "left",
              margin: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 900,
                background: "#000000",
                color: "#FFE600",
                padding: "2px 8px",
                display: "inline",
                border: "2px solid #000000",
                boxShadow: "2px 2px 0px #000000",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        {/* Brutalist Flat Progress Bar */}
        <div
          style={{
            width: "100%",
            height: "10px",
            background: "#000000",
            border: "2px solid #000000",
            borderRadius: "0px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: "#FFE600",
              transition: "width 0.05s linear",
            }}
          />
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
